import { useState, useRef, useEffect } from 'react';

/**
 * Counts a numeric value up from zero when scrolled into view.
 * Preserves any non-numeric prefix/suffix (e.g. "700+", "99.9%", "24/7").
 */
const CountUp = ({ value, duration = 1600 }) => {
  const ref = useRef(null);
  const match = String(value).match(/^([^\d]*)([\d.]+)(.*)$/);
  const zeroState = match ? `${match[1]}0${match[3]}` : String(value);
  const [display, setDisplay] = useState(zeroState);

  useEffect(() => {
    const el = ref.current;
    if (!el || !match) return;
    const [, prefix, numStr, suffix] = match;
    const target = parseFloat(numStr);
    const decimals = (numStr.split('.')[1] || '').length;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(el);
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(`${prefix}${(target * eased).toFixed(decimals)}${suffix}`);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]); // eslint-disable-line

  return <span ref={ref}>{display}</span>;
};

export default CountUp;
